/**
 * Calendar Notes Service (Campaigns)
 */
import api from './api';
import { CalendarNote, CreateNoteData, UpdateNoteData } from '@/types/calendar-notes';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const calendarNotesService = {
  /**
   * Create a new calendar note (campaign)
   */
  createNote: async (data: CreateNoteData): Promise<CalendarNote> => {
    const { calendarId, ...noteData } = data;
    const payload = {
      ...noteData,
      startAt: typeof data.startAt === 'string' ? data.startAt : data.startAt.toISOString(),
      endAt: data.endAt
        ? typeof data.endAt === 'string'
          ? data.endAt
          : data.endAt.toISOString()
        : undefined,
    };
    const response = await api.post<ApiResponse<CalendarNote>>(
      `/api/calendars/${calendarId}/notes`,
      payload
    );
    return response.data.data;
  },

  /**
   * List calendar notes for a specific calendar and date range
   */
  listNotes: async (
    calendarId: number,
    startDate: string,
    endDate: string
  ): Promise<CalendarNote[]> => {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await api.get<ApiResponse<CalendarNote[]>>(
      `/api/calendars/${calendarId}/notes?${params.toString()}`
    );
    return response.data.data;
  },

  /**
   * Update a calendar note (campaign)
   */
  updateNote: async (
    calendarId: number,
    noteId: number,
    data: UpdateNoteData
  ): Promise<CalendarNote> => {
    const payload = {
      ...data,
      startAt: data.startAt
        ? typeof data.startAt === 'string'
          ? data.startAt
          : data.startAt.toISOString()
        : undefined,
      // null must reach the API (clears the end); undefined is dropped (unchanged)
      endAt: data.endAt === null
        ? null
        : data.endAt
          ? typeof data.endAt === 'string'
            ? data.endAt
            : data.endAt.toISOString()
          : undefined,
    };
    const response = await api.patch<ApiResponse<CalendarNote>>(
      `/api/calendars/${calendarId}/notes/${noteId}`,
      payload
    );
    return response.data.data;
  },

  /**
   * Delete a calendar note (campaign)
   */
  deleteNote: async (calendarId: number, noteId: number): Promise<void> => {
    await api.delete(`/api/calendars/${calendarId}/notes/${noteId}`);
  },
};
